import { EntityBase } from '@provision/entity-types';
import { ReactNode } from 'react';
import { pathOr, stringToPath } from 'remeda';
import { CustomViewAttribute, UIType } from 'src/graphql/entities/custom-view';
import {
  CustomAttributeColumnRenderer,
  CustomAttributesRow,
} from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { customComponentsMap } from './custom-components-map';
import { generalComponentsMap } from './general-components-map/general-components-map';

export type CustomComponentsByAttribute =
  (typeof customComponentsMap)[keyof typeof customComponentsMap];

export const getAttributeRawValue = <T extends EntityBase>(
  record: T,
  column: CustomViewAttribute,
  customAttributeRenderer?: CustomAttributeColumnRenderer,
): unknown => {
  if (customAttributeRenderer) {
    // custom attribute values are decoded from the row's customAttributes blob, not a nested path
    return customAttributeRenderer.getStringValue(record as CustomAttributesRow);
  }

  // @ts-expect-error Incorrectly derived type for nested properties path.
  return pathOr(record, stringToPath(column.accessor), '');
};

/**
 * The general renderers call hooks, so this has to run inside a component that renders
 * exactly one attribute: MRT's `Cell` on the grid, `AttributeValue` everywhere else.
 */
export const renderAttributeValue = <T extends EntityBase>({
  record,
  column,
  rawValue,
  customAttributeRenderer,
  customComponentsByAttribute,
}: {
  record: T;
  column: CustomViewAttribute;
  rawValue: unknown;
  customAttributeRenderer?: CustomAttributeColumnRenderer;
  customComponentsByAttribute?: CustomComponentsByAttribute;
}): ReactNode => {
  // custom attribute cells decode the row's customAttributes blob via the field-view registry.
  if (customAttributeRenderer) {
    return customAttributeRenderer.renderCell(record as CustomAttributesRow);
  }

  // TODO need to improve components typings so it can identify types
  // by itself without asserting it via "as"

  const renderComponent = generalComponentsMap[column.uiType as UIType.Text];
  const customComponent = customComponentsByAttribute?.[column.attribute];

  const mappedValue = customComponent?.(rawValue as string, record) ?? rawValue;

  return renderComponent?.(mappedValue as string, column) ?? (mappedValue as ReactNode);
};
